import AHDate from "_shared/dates/date";
import { formatPercent } from "_shared/percentages/percent";
import type { ProposalActorRole, ProposalNegotiationParty } from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  proposalFeeScheduleAspectText,
  proposalFeeScheduleColumnFromConflictKey,
  proposalFeeScheduleColumnLabel,
  proposalFeeScheduleLeafPayload,
  proposalFeeScheduleTeamMemberLeafPayload,
  proposalFeeScheduleTeamMemberText,
} from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import { HumanReadableProposalFieldName } from "work/proposals/proposal-fields/proposal-field";

/**
 * One vocabulary for describing a proposal change.
 *
 * Every surface that tells a human "this changed" — the per-field change history, the Changes pane, the
 * Save confirmation, the Submit dialog's tranche list — renders from this module. Before it existed the
 * app carried four independent value formatters and three field-label maps, so the same boolean read
 * "true" in one place, "Negotiable" in another, and "Waiving Conflicts Check" in a third, and the Save
 * dialog described changes in a format the history could not produce.
 *
 * The formatter is deliberately pure: callers pass in the little context it cannot derive (a person's
 * name, a collection item's label), so it can be unit-tested exhaustively and reused from a dialog, a
 * popover, or a React tree without dragging queries behind it.
 */

/**
 * What happened to a field, independent of the wire's event/operation kinds.
 *
 * "replaced" is the one action no single operation produces. A document replacement is two of them —
 * an add carrying `replacesId` and the removal of the document it names — and the user did one thing,
 * so the two are paired back into one descriptor before anything renders them. See
 * `pairProposalDocumentReplacements`.
 */
export type ProposalChangeAction = "added" | "updated" | "removed" | "cleared" | "replaced";

export type ProposalChangeDescriptor = {
  /** Stable identity for selection/dedup: the conflict key plus the collection item, when there is one. */
  key: string;
  /**
   * Every change this one row stands for, when it stands for more than itself. A paired replacement
   * is one row over two change keys, and a caller acting on the row — the submit dialog's revert
   * selection — has to act on both, or reverting a replacement would send half of it back.
   * Absent on an ordinary descriptor, which stands for its own `key` alone.
   */
  coveredKeys?: readonly string[];
  path: string;
  /** "Response Due By", "Fee Schedule", "Team" — never a raw path. */
  fieldLabel: string;
  /**
   * The collection item this change is about, by the identity the negotiation stream carries. Kept
   * beside the packed `key` because pairing one change with another — a replacement's two halves —
   * is an identity question, and unpacking an id out of a conflict key that itself contains one is
   * not the same question.
   */
  itemId?: string;
  /** The collection item a change lands on, when the field is a collection ("Essential fixed fee"). */
  itemLabel?: string;
  action: ProposalChangeAction;
  previousText?: string;
  nextText?: string;
  /**
   * The previous value as the stream carries it, and whether the source said anything about it at all.
   *
   * `previousText` cannot answer the second question: it is undefined both for a published value that
   * reads as nothing and for a line that arrived with no previous side, and those are different
   * statements — the first restores an empty field, the second restores nothing. Only the change-line
   * adapter carries these, because `ProposalChangeLine.previousValueExists` is the only source that
   * draws the distinction (see `proposal-change-restored-value.ts`, which is what needs it).
   *
   * The raw value rides along because text rendered from one leaf is not always text a reader wants: a
   * rate's leaf carries no currency, so its `previousText` reads "250" where the fee schedule reads
   * "$250.00". A caller that can supply the rest of the category renders the value itself instead.
   */
  previousValue?: unknown;
  previousValueExists?: boolean;
  /**
   * The collection item's full value, when the change is about one — the whole fee-schedule category a
   * repriced rate belongs to, say. A change addresses one leaf; a reader is being shown the thing that
   * leaf is part of, and the fee schedule's renderer draws the whole category with the changed columns
   * blue-lined inside it (#4845). Supplied by {@link ProposalChangeDescriptionContext.resolveItemValue},
   * so a caller without the projection to hand simply gets the changed columns and nothing more.
   */
  itemValue?: unknown;
  party?: ProposalNegotiationParty;
  actorName?: string;
  sequence?: number;
  createdDate?: string;
};

/** Context the formatter cannot derive on its own. */
export type ProposalChangeDescriptionContext = {
  /** Resolves a user id to a display name (team/reviewer changes render people, not ids). */
  resolveUserName?: (userId: string) => string | undefined;
  /** Resolves a collection item id to its label (a fee category's name, a document's file name). */
  resolveItemLabel?: (path: string, itemId: string) => string | undefined;
  /**
   * Resolves a collection item id to its whole current value — see {@link ProposalChangeDescriptor.itemValue}.
   * Sibling of `resolveItemLabel` and resolved from the same projection index, because the same answer
   * ("which item is this, and what does it look like") is wanted at two different granularities.
   */
  resolveItemValue?: (path: string, itemId: string) => unknown;
};

export type ProposalChangeActorContext = {
  actorRole?: ProposalActorRole | null;
  resolvedName?: string;
  sourceParty?: ProposalNegotiationParty | null;
};

/**
 * Human-facing actor text shared by every proposal-change surface.
 *
 * System events deliberately retain the user IDs of the command that caused them, so role must win
 * over a resolved user name. Conversely, a missing user/party is not evidence of a System action.
 */
export function proposalChangeActorLabel({ actorRole, resolvedName, sourceParty }: ProposalChangeActorContext): string {
  if (actorRole === "System") return "Automatically";
  return resolvedName?.trim() || sourceParty || "Unknown User";
}

/** Creation-state wording shared by the history panel and revert menu. */
export const proposalCreationBaselineLabel = "Original value";
/** User-facing action for a recorded resolution decision; wire enum names never reach the UI. */
export function proposalResolutionDecisionAction(disposition: unknown): string {
  switch (disposition) {
    case "AcceptIncoming":
      return "accepted the proposed value for";
    case "KeepPrevious":
      return "kept the previous value for";
    case "ProposeAlternative":
      return "proposed an alternative for";
    case "Unresolved":
      return "reopened the decision for";
    default:
      // Historical or partially deployed entries may omit the disposition. Keep them readable without
      // inventing an outcome or exposing an implementation value.
      return "recorded a resolution decision for";
  }
}

/**
 * "Added" / "Updated" / "Removed" / "Cleared" — the one place these words are chosen.
 *
 * The team leader is a single seat rather than a list, so it is set and removed rather than added and
 * cleared; the change history already words it that way ("Set Team Leader"), and `path` is what lets this
 * agree with it.
 */
export function proposalChangeActionLabel(action: ProposalChangeAction, path?: string | null): string {
  if (path && teamLeaderPathPattern.test(path)) {
    if (action === "added") return "Set";
    if (action === "cleared") return "Removed";
  }
  return actionLabels[action];
}

const actionLabels: Record<ProposalChangeAction, string> = {
  added: "Added",
  updated: "Updated",
  removed: "Removed",
  cleared: "Cleared",
  // One document standing in for another: "Replaced Conflicts Document: original.pdf → new.pdf".
  replaced: "Replaced",
};

/**
 * The one-line form used in dialogs and summaries: "Response Due By Added: 26 July, 2026",
 * "Description Updated: Old scope → New scope", "Fee Schedule Removed: Essential fixed fee".
 *
 * The history popover renders the same descriptor with richer affordances (struck-through previous
 * value, avatars, profile links) — the words are identical, only the presentation differs.
 */
export function formatProposalChangeLine(descriptor: ProposalChangeDescriptor): string {
  const headline = proposalChangeHeadline(descriptor);
  const detail = proposalChangeDetailText(descriptor);
  return detail === undefined ? headline : `${headline}: ${detail}`;
}

/**
 * The headline half, verb first: "Added Fee Schedule", "Set Team Leader", "Updated Response Due By".
 *
 * This is the change history's word order, and it is shared with it deliberately. Rendering the subject
 * first instead ("Fee Schedule — Essential fixed fee Added") put the verb after a name it did not govern,
 * and collided outright when an item's own name ended in a verb: renaming a category to "…Category
 * Updated" and adding it read "Fee Schedule — Standalone From Existing Category Updated Added".
 *
 * The item is not in the headline at all now — it is the value, and {@link proposalChangeDetailText}
 * renders it there, exactly as the history renders "Added Team Member: Jane Doe".
 */
export function proposalChangeHeadline(descriptor: ProposalChangeDescriptor): string {
  return `${proposalChangeActionLabel(descriptor.action, descriptor.path)} ${descriptor.fieldLabel}`;
}

/**
 * The value half: what the change is about, then how it moved.
 *
 * The item is the value, never part of the headline — "Added Fee Schedule Category: Example Category 2",
 * the same shape the history's "Added Team Member: Jane Doe" uses. When the item ALSO moved a value, the
 * transition trails it in parentheses rather than replacing it, so a change never loses the name of the
 * thing it happened to: "Updated Reviewer: Jane Doe (Comment only → Full edit)".
 */
export function proposalChangeDetailText(descriptor: ProposalChangeDescriptor): string | undefined {
  const transition = formatProposalChangeDetail(descriptor);
  const { itemLabel } = descriptor;
  if (itemLabel === undefined) return transition;
  if (transition === undefined) return itemLabel;
  // The transition already says the name, so saying it again is the duplicate. `transition === itemLabel`
  // also covers a removal, whose only value is the departing item's own name.
  if (transition === itemLabel || proposalChangeTransitionCarriesItemLabel(descriptor)) return transition;
  return `${itemLabel} (${transition})`;
}

/**
 * Whether the transition already carries the item's own name: an add, whose value IS its name, or a
 * rename, whose new value is the very label {@link ProposalChangeDescriptionContext.resolveItemLabel}
 * resolves off the EFFECTIVE projection (projection plus pending operations).
 *
 * Exported so the text form and the rendered summary list apply one rule rather than each carrying its
 * own copy. Testing only `transition === itemLabel` caught the add — where there is no previous value,
 * so the transition is the bare name — but not the rename, where a previous value turns the transition
 * into "old → new" and the label stops matching it while still being fully contained in it. That is what
 * printed a renamed fee category's name twice: "Standalone From Existing Category Updated (Standalone
 * From Existing Category → Standalone From Existing Category Updated)".
 */
export function proposalChangeTransitionCarriesItemLabel(descriptor: ProposalChangeDescriptor): boolean {
  return descriptor.itemLabel !== undefined && descriptor.nextText === descriptor.itemLabel;
}

/**
 * How "there is no value here" reads. A field the change leaves empty — a date nobody set, a member who
 * is no longer on the team — has nothing to print, and printing nothing is what made a submission list
 * show bare field names with a blank where their value should be: the reader cannot tell an empty value
 * from a value the dialog failed to render. Spelled once here so every surface says it the same way.
 */
export const proposalEmptyValueText = "[Empty]";

/**
 * How a reviewer's edit rights read. One vocabulary for both surfaces that describe them: the queued
 * change a Save is about to make ("Added Reviewer: Jane Doe (Full edit)") and the history entry it
 * becomes, so a leader who grants edit rights sees the same two words before and after the save.
 */
export function proposalReviewerAccessLabel(canEdit: boolean): string {
  return canEdit ? "Full edit" : "Comment only";
}

/**
 * The two halves of a redline, with the absent side named rather than left blank.
 *
 * Only the NEW value is ever substituted. An addition genuinely has no "before", and drawing a struck
 * through "[Empty]" in front of every newly set field would be noise on the most ordinary change there
 * is; a change with nothing on the new side, on the other hand, is exactly the one a reader needs told.
 */
export function proposalChangeRedlineValues(descriptor: ProposalChangeDescriptor): {
  previousValue?: string;
  nextValue: string;
} {
  return {
    previousValue: descriptor.previousText,
    nextValue: descriptor.nextText ?? proposalEmptyValueText,
  };
}

/** The value half of a change line: "26 July, 2026", "Old scope → New scope", "$1,000.00". */
export function formatProposalChangeDetail(descriptor: ProposalChangeDescriptor): string | undefined {
  const { previousText, nextText } = descriptor;
  if (previousText && nextText) return `${previousText} → ${nextText}`;
  return nextText ?? previousText;
}

export function formatProposalChangeLines(descriptors: readonly ProposalChangeDescriptor[]): string[] {
  return descriptors.map(formatProposalChangeLine);
}

/**
 * Human-readable label for a semantic negotiation path. Resolves by leading segment so a descendant
 * path ("/discount/percent") labels as its field ("Discount"), and falls back to a title-cased path
 * rather than showing the raw pointer.
 */
export function proposalChangeFieldLabel(path?: string | null, conflictKey?: string | null): string {
  if (!path) return "Proposal";
  const memberSubject = collectionMemberLabel(path);
  if (memberSubject) {
    const aspect = proposalChangeAspectLabel(path, conflictKey);
    return aspect ? `${memberSubject} ${aspect}` : memberSubject;
  }

  const segments = path.replace(/^\/+/, "").split("/").filter(Boolean);
  const head = segments[0];
  const known = head ? proposalChangeFieldLabelsByPathSegment[head] : undefined;
  if (known) return known;
  return segments.map(titleCaseSegment).join(" ") || "Proposal";
}

/**
 * The semantic leaf within a collection item, when its conflict key addresses one.
 *
 * Collection events deliberately keep the stable collection path and item id; the conflict key is
 * what distinguishes a fee category's description from its type or rate. Keeping these words
 * here means timeline grouping, Save, and Submit all name those leaves from the same vocabulary.
 */
export function proposalChangeAspectLabel(path?: string | null, conflictKey?: string | null): string | undefined {
  if (path !== "/feeSchedule/categories") return undefined;
  // The team member is a column too: without it a reassignment announced itself as "Updated Fee
  // Schedule Category", the one column change the schedule could not name.
  const column = proposalFeeScheduleColumnFromConflictKey(conflictKey);
  return column ? proposalFeeScheduleColumnLabel(column) : undefined;
}

const teamLeaderPathPattern = /^\/(?:client|vendor)Team\/leader(?:\/|$)/;

/**
 * A change inside a collection is about the MEMBER, not the collection.
 *
 * Every one of these paths resolved by leading segment to the container's name, so adding one fee
 * category announced "Added Fee Schedule" — as though the whole schedule had been replaced — and
 * choosing a leader plus two members produced three interchangeable "Team Added" rows. The change
 * history already names the member ("Added Team Member: Jane"); this is the same vocabulary, so the two
 * surfaces describe one change with one noun.
 */
const collectionMemberLabels: ReadonlyArray<readonly [RegExp, string]> = [
  [teamLeaderPathPattern, HumanReadableProposalFieldName.teamLeader],
  [/^\/(?:client|vendor)Team\/members(?:\/|$)/, "Team Member"],
  [/^\/feeSchedule\/categories(?:\/|$)/, "Fee Schedule Category"],
  [/^\/clientPolicies\/documents(?:\/|$)/, "Client Policy Document"],
  [/^\/vendorPolicies\/documents(?:\/|$)/, "Vendor Policy Document"],
  [/^\/conflicts\/documents(?:\/|$)/, "Conflicts Document"],
];

function collectionMemberLabel(path: string): string | undefined {
  return collectionMemberLabels.find(([pattern]) => pattern.test(path))?.[1];
}

const proposalChangeFieldLabelsByPathSegment: Readonly<Record<string, string>> = {
  name: HumanReadableProposalFieldName.name,
  description: HumanReadableProposalFieldName.description,
  negotiable: HumanReadableProposalFieldName.negotiable,
  responseDueBy: HumanReadableProposalFieldName.responseDueBy,
  startDate: HumanReadableProposalFieldName.startDate,
  endDate: HumanReadableProposalFieldName.endDate,
  vendorTeam: HumanReadableProposalFieldName.team,
  clientTeam: HumanReadableProposalFieldName.team,
  teamRestricted: HumanReadableProposalFieldName.teamRestriction,
  feeSchedule: HumanReadableProposalFieldName.feeSchedule,
  clientPolicies: HumanReadableProposalFieldName.clientPolicies,
  vendorPolicies: HumanReadableProposalFieldName.vendorPolicies,
  conflicts: HumanReadableProposalFieldName.conflicts,
  conflictsCheckWaived: HumanReadableProposalFieldName.waiveConflictsCheck,
  discount: HumanReadableProposalFieldName.discount,
  clientReviewers: "Reviewers",
  vendorReviewers: "Reviewers",
};

function titleCaseSegment(segment: string): string {
  return segment
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (character) => character.toUpperCase());
}

/**
 * Renders a stored value the way the field itself reads.
 *
 * Dates become calendar dates ("26 July, 2026") rather than the stored ISO datetime, money renders with
 * its currency, percentages keep their sign, and booleans read as the field's own words ("Negotiable" /
 * "Not Negotiable") instead of "true"/"false". A structured value with no readable form returns
 * undefined so callers fall back to the action phrase instead of dumping JSON at the user.
 */
export function proposalChangeValueText(
  path: string | null | undefined,
  value: unknown,
  context: ProposalChangeDescriptionContext = {},
): string | undefined {
  if (value === undefined || value === null) return undefined;

  // A collection leaf is its column's value wrapped back up in the column's own name — the shape the
  // command builder emits and the server stores (`{ "feeRate": 125 }`). Read as an object it is no
  // shape at all: only `name` happened to be a key `objectValueText` probes for, so a repriced category,
  // a recoded one and a re-described one each rendered as their column's label with nothing after it,
  // in the history, the save confirmation and the submission summary alike. Unwrap it first and the
  // ordinary scalar rendering below does the rest.
  const feeScheduleAspect = proposalFeeScheduleLeafPayload(path, value);
  if (feeScheduleAspect) {
    return proposalFeeScheduleAspectText(feeScheduleAspect.aspect, feeScheduleAspect.value);
  }
  // The assigned member is the one column whose value is a person rather than its own text: two ids
  // the shared object rendering below could not name (they sit one level down, under `teamMember`), so
  // a reassignment read as nothing here and "(changed)" in the history (#4949). It reads as the person
  // now — resolved through the same context that names a team-roster change — or as "Unassigned".
  const feeScheduleTeamMember = proposalFeeScheduleTeamMemberLeafPayload(path, value);
  if (feeScheduleTeamMember) {
    return proposalFeeScheduleTeamMemberText(feeScheduleTeamMember.value, context.resolveUserName);
  }

  if (typeof value === "boolean") return booleanValueText(path, value);
  if (typeof value === "number") return numberValueText(path, value);
  if (typeof value === "string") return stringValueText(path, value);
  if (Array.isArray(value)) return value.length === 0 ? "None" : `${value.length} entries`;
  if (typeof value === "object") return objectValueText(path, value as Record<string, unknown>, context);

  return undefined;
}

const truthyByPathSegment: Readonly<Record<string, readonly [string, string]>> = {
  // [truthy, falsy] — the words each flag reads as in its own field.
  negotiable: ["Negotiable", "Not Negotiable"],
  conflictsCheckWaived: ["Waiving Conflicts Check", "Not Waiving Conflicts Check"],
  teamRestricted: [
    "Team members require renegotiation to modify",
    "Team members can be modified freely without renegotiation",
  ],
  isEditing: ["Full edit", "Comment only"],
};

/**
 * Both candidate segments are looked up, not merely the first that EXISTS.
 *
 * A flag's canonical negotiation path addresses the scalar inside its wrapper — `/conflictsCheckWaived/value`,
 * `/teamRestricted/value` (see `ProposalCollaborationPathPolicy.FieldPaths`) — so the last segment is the
 * meaningless "value". `lastPathSegment(path) ?? leadingPathSegment(path)` returned that string happily,
 * and `??` never fell through, so the map was consulted for a key it could not hold and every one of these
 * flags read "Yes"/"No". `negotiable` is the only one with no wrapper, which is why the map looked correct:
 * the save confirmation announced "Updated Waive Conflicts Check: No → Yes" while the switch beside it,
 * the field history and the submit dialog all said "Waiving Conflicts Check" — the exact split this
 * module's header describes as the reason it exists.
 */
function booleanValueText(path: string | null | undefined, value: boolean): string {
  const words =
    truthyByPathSegment[lastPathSegment(path) ?? ""] ?? truthyByPathSegment[leadingPathSegment(path) ?? ""];
  if (words) return value ? words[0] : words[1];
  return value ? "Yes" : "No";
}

function numberValueText(path: string | null | undefined, value: number): string {
  if (pathMentions(path, "percent") || pathMentions(path, "discount")) return percentValueText(value);
  return String(value);
}

/**
 * A percentage is STORED as a fraction — `Percent` enforces 0..1 — and read out multiplied by 100.
 * Printing the stored number raw made a 4% discount announce itself as "0.04%", in the one module
 * whose whole purpose is that a value reads the same everywhere it appears.
 *
 * The shape comes from `Percent.toString()` rather than being spelled again here, so this cannot drift
 * from what the discount field itself renders — which is exactly how it drifted in the first place.
 */
function percentValueText(value: number): string {
  // A change description must never throw — it is often the only thing telling a user what they are
  // about to save — so the formatter also renders out-of-range values instead of constructing Percent.
  return formatPercent(value);
}

function stringValueText(path: string | null | undefined, value: string): string | undefined {
  if (!value) return undefined;
  if (pathIsDate(path)) return formatProposalChangeDate(value);
  return truncateForDisplay(value);
}

function objectValueText(
  path: string | null | undefined,
  value: Record<string, unknown>,
  context: ProposalChangeDescriptionContext,
): string | undefined {
  // Money and percent arrive as {amount, currency} / {value} wrappers.
  const money = moneyValueText(value);
  if (money) return money;

  if (typeof value.value === "number" || typeof value.value === "string") {
    return proposalChangeValueText(path, value.value, context);
  }

  const userId = firstString(value, ["userId", "reviewerUserId"]);
  if (userId) {
    const name = context.resolveUserName?.(userId);
    if (name) return name;
  }

  const label = firstString(value, ["name", "title", "displayName", "fileName", "categoryName"]);
  if (label) return truncateForDisplay(label);

  // A proposal document is a reference — `{ documentId, documentInfo }` — and the file's name lives one
  // level down on the info. Reading only the top level left every policy and conflicts document change
  // with no value at all beside it, which is precisely the row where the file's name is the whole point.
  const documentName = nestedRecord(value, "documentInfo");
  if (documentName) {
    const name = firstString(documentName, ["name", "title", "displayName", "fileName"]);
    if (name) return truncateForDisplay(name);
  }

  return undefined;
}

function nestedRecord(value: Record<string, unknown>, key: string): Record<string, unknown> | undefined {
  const nested = value[key];
  if (!nested || typeof nested !== "object" || Array.isArray(nested)) return undefined;
  return nested as Record<string, unknown>;
}

function moneyValueText(value: Record<string, unknown>): string | undefined {
  const amount = value.amount ?? value.value;
  const currency = value.currency;
  if (typeof amount !== "number" || typeof currency !== "string") return undefined;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

/**
 * Calendar date for display — "26 July, 2026". Proposal dates are stored as full ISO datetimes; showing
 * the raw value (or a timezone-shifted one) is what made a due date read as the day before.
 */
export function formatProposalChangeDate(value: string): string {
  const date = new AHDate(value);
  if (!date.isValid) return value;
  return date.format("D MMMM, YYYY");
}

const dateFieldSegments: ReadonlySet<string> = new Set(["startDate", "endDate", "responseDueBy", "dateApproved"]);

function pathIsDate(path?: string | null): boolean {
  const segment = leadingPathSegment(path);
  const tail = lastPathSegment(path);
  return Boolean((segment && dateFieldSegments.has(segment)) || (tail && dateFieldSegments.has(tail)));
}

function pathMentions(path: string | null | undefined, segment: string): boolean {
  if (!path) return false;
  return path.toLowerCase().includes(segment.toLowerCase());
}

function leadingPathSegment(path?: string | null): string | undefined {
  if (!path) return undefined;
  return path.replace(/^\/+/, "").split("/").filter(Boolean)[0];
}

function lastPathSegment(path?: string | null): string | undefined {
  if (!path) return undefined;
  const segments = path.replace(/^\/+/, "").split("/").filter(Boolean);
  return segments.at(-1);
}

function firstString(value: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const candidate = value[key];
    if (typeof candidate === "string" && candidate) return candidate;
  }
  return undefined;
}

const displayValueMaxLength = 80;

function truncateForDisplay(value: string): string {
  const collapsed = value.replace(/\s+/g, " ").trim();
  if (collapsed.length <= displayValueMaxLength) return collapsed;
  return `${collapsed.slice(0, displayValueMaxLength - 1)}…`;
}

/** The action a change kind represents, collapsing the wire's operation and event kinds onto one set. */
export function proposalChangeActionFor(args: {
  kind: string;
  previousExists: boolean;
  nextExists: boolean;
}): ProposalChangeAction {
  const { kind, previousExists, nextExists } = args;
  if (kind === "CollectionItemAdded") return "added";
  if (kind === "CollectionItemRemoved") return "removed";
  if (!nextExists && previousExists) return "cleared";
  if (nextExists && !previousExists) return "added";
  return "updated";
}

/** Identity used for selection and dedup across surfaces (tranche checkboxes, badge grouping). */
export function proposalChangeKey(conflictKey: string, itemId?: string | null): string {
  return itemId ? `${conflictKey}|${itemId}` : conflictKey;
}
